import { useCallback, useMemo } from 'react'

import { slugify } from '../utils/slugify.js'

import { useQueryParams, UseQueryParamsOptions } from './useQueryParams.js'

export type QueryParamName = {
  name: string
  property?: string
}

export const useQueryParam = <T = unknown>(
  { name, property }: QueryParamName,
  {
    navigationMode = 'push',
    parseOptions,
    stringifyOptions,
  }: UseQueryParamsOptions = {},
): readonly [T, (newValue: T) => void] => {
  const [queryParams, setQueryParams] = useQueryParams({
    navigationMode,
    parseOptions,
    stringifyOptions,
  })

  const firstLevel = slugify(name)
  const secondLevel = property ? slugify(property) : undefined

  const value = useMemo<T>(() => {
    const queryParam = queryParams[firstLevel]

    if (secondLevel) {
      return queryParam?.[secondLevel]
    }

    return queryParam
  }, [firstLevel, secondLevel, queryParams])

  const setValue = useCallback(
    (newValue: T) => {
      if (!secondLevel) {
        setQueryParams({
          [firstLevel]: newValue,
        })
        return
      }

      const queryParam = queryParams[firstLevel] ?? {}
      if (typeof queryParam === 'object') {
        setQueryParams({
          [firstLevel]: {
            ...queryParam,
            [secondLevel]: newValue,
          },
        })
      }
    },
    [firstLevel, secondLevel, queryParams, setQueryParams],
  )

  return [value, setValue] as const
}
